import { useParams } from '@tanstack/react-router'

import { useModal } from '@/components/modals/modal-container'

interface UserChipProps {
  userId: number
  /** Display name (already tag-stripped via clean()). Optional — chip falls
   *  back to "User" if not provided. */
  name?: string
  /** Tone variant. `inline` keeps the chip flush with surrounding text;
   *  `block` adds the user-id suffix on its own line (used in summaries
   *  where the chip is the primary element). */
  variant?: 'inline' | 'block'
}

/**
 * Clickable user pill that opens the existing user modal. Used in chat
 * summaries and the structured details view wherever a `user_id` + name
 * pair appears. The chat drawer stays mounted underneath the modal — the
 * operator can close the modal and resume the conversation without
 * losing chat state.
 *
 * Gets `eventId` from the route, not via prop, so this component can be
 * dropped into any summary without threading props down the tool-result
 * render chain. Safe because every rendered chat lives under the
 * `/_authenticated/events/$eventId` route by construction.
 */
export function UserChip({ userId, name, variant = 'block' }: UserChipProps) {
  const { openUserModal } = useModal()
  const params = useParams({ from: '/_authenticated/events/$eventId' })
  const eventIdNum = Number(params.eventId)

  const display = name && name.trim().length > 0 ? name.trim() : `User`

  return (
    <button
      type="button"
      onClick={() => openUserModal(userId, eventIdNum)}
      className="inline-flex items-baseline gap-1 rounded font-medium text-neutral-900 underline decoration-dotted decoration-neutral-400 underline-offset-2 transition-colors hover:text-blue-700 hover:decoration-blue-600 dark:text-neutral-100 dark:decoration-neutral-500 dark:hover:text-blue-300 dark:hover:decoration-blue-400"
      aria-label={`Open ${display} (user #${userId}) details`}
      title="Open attendee details"
    >
      <span>{display}</span>
      {variant === 'block' && (
        <span className="text-neutral-500 no-underline dark:text-neutral-400">
          #{userId}
        </span>
      )}
    </button>
  )
}
